<template>
    <div v-if="visibleSlides.length" class="task-list-promo">
        <div class="task-list-promo__wrap">
            <div
                class="task-list-promo__stage fr-wasabi"
                @mouseenter="pauseRotation"
                @mouseleave="resumeRotation"
            >
                <div
                    v-for="(slide, index) in slides"
                    :key="slide.id"
                    class="task-list-promo__ban"
                    :class="[
                        slide.theme,
                        { 'f-bar': slide.frameBar, on: index === activeIndex },
                    ]"
                >
                    <span class="task-list-promo__tag">Реклама</span>

                    <div
                        v-if="slide.motif === 'dots'"
                        class="task-list-promo__m-dots task-list-promo__m-dots--indigo"
                    />
                    <div v-else-if="slide.motif === 'slash'" class="task-list-promo__m-slash" />
                    <div
                        v-else-if="slide.motif === 'rings'"
                        class="task-list-promo__m-rings task-list-promo__m-rings--ink"
                    />
                    <div
                        v-else-if="slide.motif === 'bars'"
                        class="task-list-promo__m-bars task-list-promo__m-bars--indigo"
                    >
                        <i style="height: 22px" />
                        <i style="height: 36px" />
                        <i style="height: 28px" />
                        <i style="height: 50px" />
                        <i style="height: 42px" />
                        <i style="height: 64px" />
                    </div>

                    <div class="task-list-promo__ico" aria-hidden="true">{{ slide.icon }}</div>
                    <div class="task-list-promo__body">
                        <span class="task-list-promo__badge">{{ slide.badge }}</span>
                        <div class="task-list-promo__title">{{ slide.title }}</div>
                        <div class="task-list-promo__desc">{{ slide.description }}</div>
                    </div>
                    <button type="button" class="task-list-promo__btn" @click="onCtaClick(slide)">
                        {{ slide.ctaLabel }}
                    </button>
                </div>
            </div>

            <div class="task-list-promo__ctrl">
                <div class="task-list-promo__dots">
                    <button
                        v-for="(_, index) in slides"
                        :key="index"
                        type="button"
                        class="task-list-promo__dot"
                        :class="{ on: index === activeIndex }"
                        :aria-label="`Слайд ${index + 1}`"
                        @click="goToSlide(index)"
                    />
                </div>
                <div class="task-list-promo__ctrl-right">
                    <span class="task-list-promo__rot-label">{{ rotationLabel }}</span>
                    <div class="task-list-promo__arrows">
                        <button
                            type="button"
                            class="task-list-promo__arrow"
                            aria-label="Предыдущий слайд"
                            @click="prev"
                        >
                            <v-icon size="18">mdi-chevron-left</v-icon>
                        </button>
                        <button
                            type="button"
                            class="task-list-promo__arrow"
                            aria-label="Следующий слайд"
                            @click="next"
                        >
                            <v-icon size="18">mdi-chevron-right</v-icon>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import config from '@root/config'
import { filterUninstalledPromos } from '@/api/marketplace'
const ROTATION_MS = 6000

const SLIDES = [
    {
        id: 'crm',
        theme: 't-light',
        frameBar: true,
        motif: 'dots',
        icon: '⚙️',
        badge: 'Маркетплейс',
        title: 'Нет времени настраивать рассылки?',
        description:
            'Эксперты Data Heroes возьмут на себя всё — от сегментов до автокасаний и даже обучат ваших администраторов',
        ctaLabel: 'Узнать подробнее',
        marketplaceId: config.marketplaceModulesIds[config.buildType].crmSettings,
    },
    {
        id: 'intensive',
        theme: 't-ink',
        frameBar: false,
        motif: 'slash',
        icon: '🔥',
        badge: 'Маркетплейс',
        title: 'Выручки растут, когда растут люди',
        description:
            'Интенсив по продажам для администраторов: отработка возражений и техники перезаписи',
        ctaLabel: 'Узнать подробнее',
        marketplaceId: config.marketplaceModulesIds[config.buildType].salesTraining,
    },
    {
        id: 'kpi',
        theme: 't-ink',
        frameBar: false,
        motif: 'rings',
        icon: '🎯',
        badge: 'Эксперт',
        title: 'Кто реально работает с задачами, а кто ставит галочки?',
        description:
            'Один созвон с экспертом — у администраторов новая мотивация с конверсией и бонусом',
        ctaLabel: 'Записаться на созвон',
        marketplaceId: config.marketplaceModulesIds[config.buildType].kpiSettings,
    },
    {
        id: 'autopilot',
        theme: 't-light',
        frameBar: true,
        motif: 'bars',
        icon: '🚀',
        badge: 'Маркетплейс',
        title: 'Бьюти-автопилот: администраторы работают — вы контролируете',
        description: 'Авто-задачи для команды, мотивация за результат и отслеживание прямо в DH',
        ctaLabel: 'Узнать подробнее',
        marketplaceId: config.marketplaceModulesIds[config.buildType].beautyAutopilot,
    },
]

export default {
    name: 'TaskListPromoCarousel',
    props: {
        projectId: {
            type: String,
            required: true,
        },
        /** Если задан — показываются только слайды с перечисленными id (crm, intensive, kpi, autopilot). */
        slideIds: {
            type: Array,
            default: null,
        },
    },
    data() {
        return {
            activeIndex: 0,
            rotationTimer: null,
            visibleSlides: [],
        }
    },
    computed: {
        sourceSlides() {
            if (!this.slideIds?.length) {
                return SLIDES
            }
            const ids = new Set(this.slideIds)
            return SLIDES.filter(slide => ids.has(slide.id))
        },
        slides() {
            return this.visibleSlides
        },
        rotationLabel() {
            return `${this.activeIndex + 1} из ${this.slides.length}`
        },
    },
    watch: {
        slides: {
            handler(slides) {
                if (!slides.length) {
                    this.activeIndex = 0
                    this.clearRotation()
                    return
                }
                if (this.activeIndex >= slides.length) {
                    this.activeIndex = 0
                }
                this.resetRotation()
            },
        },
    },
    async mounted() {
        this.visibleSlides = await filterUninstalledPromos(this.projectId, this.sourceSlides)
        if (this.visibleSlides.length) {
            this.resetRotation()
        }
    },
    beforeDestroy() {
        this.clearRotation()
    },
    methods: {
        show(index) {
            const count = this.slides.length
            this.activeIndex = ((index % count) + count) % count
        },
        next() {
            this.show(this.activeIndex + 1)
            this.resetRotation()
        },
        prev() {
            this.show(this.activeIndex - 1)
            this.resetRotation()
        },
        goToSlide(index) {
            this.show(index)
            this.resetRotation()
        },
        clearRotation() {
            if (this.rotationTimer) {
                clearInterval(this.rotationTimer)
                this.rotationTimer = null
            }
        },
        resetRotation() {
            this.clearRotation()
            if (this.slides.length < 2) {
                return
            }
            this.rotationTimer = setInterval(() => {
                this.show(this.activeIndex + 1)
            }, ROTATION_MS)
        },
        pauseRotation() {
            this.clearRotation()
        },
        resumeRotation() {
            this.resetRotation()
        },
        onCtaClick(slide) {
            if (!this.projectId || !slide.marketplaceId) return
            const targetPath = `/project/${this.projectId}/marketplace/integration/${slide.marketplaceId}`
            const { href } = this.$router.resolve(targetPath)
            window.open(href, '_blank', 'noopener,noreferrer')
        },
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'

.task-list-promo
    margin: 18px 0 4px
    max-width: 1016px

.task-list-promo__wrap
    position: relative

.task-list-promo__stage
    position: relative
    height: 120px
    border-radius: 12px
    overflow: hidden

.task-list-promo__ban
    position: absolute
    inset: 0
    display: flex
    align-items: center
    gap: 20px
    padding: 0 24px
    opacity: 0
    visibility: hidden
    transition: opacity .55s ease
    border-radius: 12px
    overflow: hidden

    &.on
        opacity: 1
        visibility: visible

.task-list-promo__ico
    width: 56px
    height: 56px
    border-radius: 14px
    display: flex
    align-items: center
    justify-content: center
    font-size: 26px
    flex-shrink: 0
    position: relative
    z-index: 3

.task-list-promo__body
    flex: 1
    min-width: 0
    position: relative
    z-index: 3

.task-list-promo__badge
    display: inline-flex
    align-items: center
    gap: 4px
    font-family: $font-family-base
    font-size: 9.5px
    font-weight: 800
    letter-spacing: .08em
    text-transform: uppercase
    padding: 3px 10px
    border-radius: 999px
    margin-bottom: 7px

.task-list-promo__title
    font-family: $font-family-base
    font-size: 18px
    font-weight: 900
    letter-spacing: -.02em
    line-height: 1.18
    margin-bottom: 5px

.task-list-promo__desc
    font-family: $font-family-base
    font-size: 12.5px
    font-weight: 500
    line-height: 1.45
    // max-width: 560px

.task-list-promo__btn
    flex-shrink: 0
    position: relative
    z-index: 3
    border: none
    border-radius: 9px
    cursor: pointer
    font-family: $font-family-base
    font-weight: 800
    font-size: 13px
    letter-spacing: -.01em
    padding: 12px 22px
    white-space: nowrap
    transition: filter .15s ease, transform .15s ease

    &:hover
        filter: brightness(.95)
        transform: translateY(-1px)

.task-list-promo__tag
    position: absolute
    top: 9px
    right: 13px
    z-index: 4
    font-family: $font-family-base
    font-size: 8px
    font-weight: 800
    letter-spacing: .12em
    text-transform: uppercase
    opacity: .5

.task-list-promo__ban.t-ink
    background: $dark-new
    border: 1px solid #2b2b46

    .task-list-promo__ico
        background: rgba($main-new, .2)
        border: 1px solid rgba($main-new, .3)
        color: $pop-new

    .task-list-promo__badge
        background: rgba($pop-new, .14)
        color: $pop-new

    .task-list-promo__title
        color: #fff

    .task-list-promo__desc
        color: rgba(255, 255, 255, .58)

    .task-list-promo__btn
        background: $pop-new
        color: #1A1800
        box-shadow: 0 2px 14px rgba($pop-new, .4)

    .task-list-promo__tag
        color: rgba(255, 255, 255, .4)

.task-list-promo__ban.t-light
    background: #fff
    border: 1px solid $border-new

    .task-list-promo__ico
        background: $dark-new
        color: $pop-new

    .task-list-promo__badge
        background: $bcg-card-new
        color: $main-new

    .task-list-promo__title
        color: $dark-new

    .task-list-promo__desc
        color: rgba($dark-new, .56)

    .task-list-promo__btn
        background: $pop-new
        color: #1A1800
        box-shadow: 0 2px 12px rgba($pop-new, .45)

    .task-list-promo__tag
        color: rgba($dark-new, .35)

.task-list-promo__ban.f-bar
    padding-left: 30px

    &::before
        content: ''
        position: absolute
        left: 0
        top: 0
        bottom: 0
        width: 6px
        background: $pop-new
        z-index: 4

.task-list-promo__m-bars
    position: absolute
    right: 30px
    bottom: 0
    display: flex
    align-items: flex-end
    gap: 6px
    height: 70px
    z-index: 1
    pointer-events: none

    i
        width: 11px
        border-radius: 3px 3px 0 0
        display: block

    &--indigo i
        background: rgba($main-new, .15)

.task-list-promo__m-rings
    position: absolute
    width: 230px
    height: 230px
    border-radius: 50%
    top: 50%
    right: -80px
    transform: translateY(-50%)
    z-index: 1
    pointer-events: none
    background: repeating-radial-gradient(circle at center, currentColor 0 1.5px, transparent 1.5px 18px)

    &--ink
        color: rgba($pop-new, .16)

.task-list-promo__m-dots
    position: absolute
    inset: 0
    z-index: 1
    pointer-events: none
    background-image: radial-gradient(currentColor 1.5px, transparent 1.5px)
    background-size: 18px 18px
    -webkit-mask-image: linear-gradient(105deg, transparent 45%, #000 100%)
    mask-image: linear-gradient(105deg, transparent 45%, #000 100%)

    &--indigo
        color: rgba($main-new, .14)

.task-list-promo__m-slash
    position: absolute
    inset: 0
    z-index: 1
    pointer-events: none
    background: repeating-linear-gradient(115deg, transparent 0 26px, rgba($pop-new, .07) 26px 31px)

.task-list-promo__stage.fr-wasabi .task-list-promo__ban
    border: 2.5px solid $pop-new
    box-shadow: 0 4px 18px rgba($pop-new, .18)
    padding-left: 24px

    &.f-bar::before
        display: none

.task-list-promo__ctrl
    display: flex
    align-items: center
    justify-content: space-between
    margin-top: 11px

.task-list-promo__dots
    display: flex
    gap: 7px

.task-list-promo__dot
    width: 7px
    height: 7px
    border-radius: 50%
    background: #CDD3DE
    cursor: pointer
    transition: .2s ease
    border: none
    padding: 0

    &.on
        background: $main-new
        width: 20px
        border-radius: 4px

.task-list-promo__ctrl-right
    display: flex
    align-items: center
    gap: 14px

.task-list-promo__rot-label
    font-family: $font-family-base
    font-size: 10px
    font-weight: 700
    letter-spacing: .05em
    text-transform: uppercase
    color: $muted-new

.task-list-promo__arrows
    display: flex
    gap: 7px

.task-list-promo__arrow
    width: 28px
    height: 28px
    border-radius: 7px
    border: 1px solid $border-new
    background: #fff
    color: $muted-new
    cursor: pointer
    display: flex
    align-items: center
    justify-content: center
    padding: 0
    transition: color .15s ease, border-color .15s ease

    &:hover
        color: $text-color-new
        border-color: #C2C8D4

    ::v-deep .v-icon
        color: inherit !important

@media (max-width: 820px)
    .task-list-promo__desc
        display: none

    .task-list-promo__m-bars
        display: none
</style>
